import React, { useMemo, useCallback, useState } from 'react';
import { Skeleton, Row, Col, Pagination, PaginationProps } from 'antd';
import {
  StyledWrapper,
  FooterWrapper,
  StyledTitle,
  PaginationWrap,
  SectionHeader,
} from './paginatingItems.styled';

const rows = [1, 2, 3, 4, 6];

function WrapperCol({
  perView,
  children,
  fullWidthMobile,
}: {
  perView: number;
  children: React.ReactNode;
  fullWidthMobile?: boolean;
}) {
  const previewIndex = rows.indexOf(perView) < 0 ? 3 : rows.indexOf(perView);
  const lg = rows[previewIndex];
  const md = rows[previewIndex - 1] < 0 ? 0 : rows[previewIndex - 1];
  const mdSpan = fullWidthMobile || md === 0 ? 24 : 24 / md;
  return (
    <Col xs={24} md={mdSpan} lg={24 / lg} style={{ display: 'flex' }}>
      {children}
    </Col>
  );
}

function LoadingCards(count: number, fullWidthMobile?: boolean) {
  return [...Array(count)].map((_, index) => (
    <WrapperCol perView={count} fullWidthMobile={fullWidthMobile} key={`loading-${count}-${index}`}>
      <Skeleton active title={false} avatar={{ shape: 'square' }} paragraph={{ rows: 4 }} />
    </WrapperCol>
  ));
}

function getItemKey(item: unknown, index: number) {
  if (item && typeof item === 'object') {
    const record = item as Record<string, any>;

    return (
      record.id || record.key || record.attributes?.id || record.attributes?.slug || record.attributes?.title || index
    );
  }

  return index;
}

function RenderedItem<T>({ item, renderList }: { item: T; renderList: (item: T) => React.ReactNode }) {
  return <>{renderList(item)}</>;
}

interface PaginatingItemsProp<T> extends PaginationProps {
  items: T[];
  renderList: (item: T) => React.ReactNode;
  title?: string;
  /** Renders on the right side of the title row (e.g. “Add” action). */
  titleExtra?: React.ReactNode;
  perView?: number; // this is for showing the grid system
  /** Keep one card per row below the large breakpoint (matches group cards on mobile). */
  fullWidthMobile?: boolean;
  loading?: boolean;
  pageSize?: number;
  firstItem?: React.ReactNode;
  controlledPaginating?: boolean; // means pagination handled from backend or it should be handle from front-end. its not good idea to handle pagination from front-end but this is just for long lists that we want to show in pages
}

function PaginatingItems<T>({
  items = [],
  title,
  titleExtra,
  renderList,
  perView = 4,
  fullWidthMobile = false,
  loading = false,
  pageSize = 18,
  controlledPaginating,
  firstItem,
  ...paginationProps
}: PaginatingItemsProp<T>) {
  const [currentPage, setPage] = useState(1);

  const onPageChange = useCallback((page: any) => {
    setPage(page);
  }, []);

  const renderedListItems = useMemo(() => {
    if (!controlledPaginating) {
      return items;
    }
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;

    return items.slice(start, end);
  }, [controlledPaginating, currentPage, items, pageSize]);

  return (
    <StyledWrapper>
      {title &&
        <SectionHeader>
          <StyledTitle>
            {title}
          </StyledTitle>
          {titleExtra && titleExtra}
        </SectionHeader>
      }
      <Row gutter={[24, 24]}>
        {firstItem && (
          <WrapperCol perView={perView} fullWidthMobile={fullWidthMobile}>
            {firstItem}
          </WrapperCol>
        )}
        {loading
          ? LoadingCards(perView, fullWidthMobile)
          : renderedListItems.map((item, index) => (
            <WrapperCol key={getItemKey(item, index)} perView={perView} fullWidthMobile={fullWidthMobile}>
              <RenderedItem item={item} renderList={renderList} />
            </WrapperCol>
          ))}
      </Row>
      <FooterWrapper>
        <PaginationWrap>
          {controlledPaginating ? (
            <Pagination pageSize={pageSize} total={items.length} current={currentPage} onChange={onPageChange} />
          ) : (
            <Pagination pageSize={pageSize} {...paginationProps} />
          )}
        </PaginationWrap>
      </FooterWrapper>
    </StyledWrapper>
  );
}

export default PaginatingItems;
